import { Button } from "@/components/ui/button";
import { Github, Linkedin, Mail, Instagram, Twitter } from "lucide-react";
import { useEffect, useState } from "react";
import { siteSettingsQuery } from "@/lib/sanity";
import { useSanityQuery } from "@/hooks/use-sanity-query";
import type { SiteSettings } from "@/types/content";

export default function HeroSection() {
  const [displayText, setDisplayText] = useState("");
  const [showCursor, setShowCursor] = useState(true);

  const { data: settings } = useSanityQuery<SiteSettings>(["sanity", "siteSettings"], siteSettingsQuery);

  const fullText = settings?.title || "Cybersecurity Engineer | Zero Trust Architect | Automation Enthusiast";
  const firstName = settings?.firstName || "Callie";
  const lastName = settings?.lastName || "Borgerding";
  const fullName = `${firstName} ${lastName}`.trim();

  useEffect(() => {
    const cancelled = { current: false };
    let currentIndex = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const typeWriter = () => {
      if (cancelled.current) return;
      if (currentIndex >= fullText.length) return;
      setDisplayText(fullText.substring(0, currentIndex + 1));
      currentIndex++;
      if (cancelled.current) return;
      timeoutId = setTimeout(typeWriter, 50);
    };

    timeoutId = setTimeout(typeWriter, 1000);
    return () => {
      cancelled.current = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [fullText]);

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 1000);
    return () => clearInterval(cursorInterval);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-screen matrix-bg flex items-center justify-center relative px-4 py-8">
      <div className="text-center z-50 animate-fade-in w-full max-w-6xl mx-auto">
        {/* Professional headshot */}
        <div className="mb-6 sm:mb-8 relative inline-block">
          <img 
            src={settings?.profileImageUrl || "https://avatars.githubusercontent.com/u/108496882?v=4"}
            alt={`${fullName} - Cybersecurity Professional`}
            className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 rounded-full mx-auto border-2 sm:border-4 border-pink-400 shadow-lg animate-glow object-cover"
          />
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-400/20 to-pink-300/20 animate-pulse"></div>
        </div>
        
        <h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-mono font-light mb-6 relative z-50 text-white tracking-tight"
          aria-label={fullName}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3">
            <span>{firstName}</span>
            <span className="hidden sm:inline" aria-hidden="true">
              {" "}
            </span>
            <span>{lastName}</span>
          </div>
        </h1>
        
        <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 font-mono text-gray-300 max-w-4xl mx-auto px-4">
          <span className="terminal-text">
            {displayText}
            {showCursor && <span className="text-pink-400">|</span>}
          </span>
        </p>
        
        <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-8 px-4">
          {settings?.githubUrl && (
            <a href={settings.githubUrl} target="_blank" rel="noopener noreferrer" className="glass-card p-3 rounded-full hover:scale-110 transition-transform">
              <Github className="w-6 h-6 text-pink-400 hover:text-pink-300" />
            </a>
          )}
          {settings?.linkedinUrl && (
            <a href={settings.linkedinUrl} target="_blank" rel="noopener noreferrer" className="glass-card p-3 rounded-full hover:scale-110 transition-transform">
              <Linkedin className="w-6 h-6 text-pink-400 hover:text-pink-300" />
            </a>
          )}
          {settings?.email && (
            <a href={`mailto:${settings.email}`} className="glass-card p-3 rounded-full hover:scale-110 transition-transform">
              <Mail className="w-6 h-6 text-pink-400 hover:text-pink-300" />
            </a>
          )}
          {settings?.instagramUrl && (
            <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" className="glass-card p-3 rounded-full hover:scale-110 transition-transform">
              <Instagram className="w-6 h-6 text-pink-400 hover:text-pink-300" />
            </a>
          )}
          {settings?.twitterUrl && (
            <a href={settings.twitterUrl} target="_blank" rel="noopener noreferrer" className="glass-card p-3 rounded-full hover:scale-110 transition-transform">
              <Twitter className="w-6 h-6 text-pink-400 hover:text-pink-300" />
            </a>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 px-4">
          <Button 
            onClick={() => scrollToSection('about')}
            className="bg-gradient-to-r from-pink-500 to-pink-400 text-white font-semibold px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:scale-105 transition-transform w-full sm:w-auto"
          >
            {settings?.heroPrimaryCta || "Explore My Work"}
          </Button>
          <Button 
            onClick={() => scrollToSection('contact')}
            variant="outline"
            className="border-pink-400 text-pink-400 hover:bg-pink-400 hover:text-white transition-colors w-full sm:w-auto"
          >
            {settings?.heroSecondaryCta || "Get In Touch"}
          </Button>
        </div>
      </div>
    </section>
  );
}
